/**
 * PublicBookingLocation — where the appointment happens, on the public page.
 *
 * A booker deciding whether to take a slot needs to know whether they are
 * driving somewhere, waiting for a call or opening a link. The address gets a
 * Google Maps link; a custom meeting link is never shown here — the server
 * hands it over only after booking, as the booker's join link.
 */
import React from 'react';

import { t } from '../../i18n';

export type PublicLocationType = 'buzzcall' | 'in_person' | 'phone' | 'custom_link';

export interface PublicLocation {
  type: PublicLocationType;
  address: string | null;
  mapsUrl: string | null;
  notes: string | null;
  /** The form must require a phone number (in-person and phone types). */
  phoneRequired: boolean;
}

/** Only a Google Maps https link is rendered as a link. */
function safeMapsUrl(url: string | null): string | null {
  return url !== null && url.startsWith('https://www.google.com/maps/') ? url : null;
}

export default function PublicBookingLocation({ location }: { location: PublicLocation }): React.ReactElement {
  const maps = safeMapsUrl(location.mapsUrl);
  return (
    <div className="mt-4 rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700" data-testid="booking-location">
      <p className="font-medium">{t(`booking.public.location.${location.type}`)}</p>
      {location.type === 'in_person' && location.address && (
        <p className="mt-0.5 whitespace-pre-line" data-testid="booking-location-address">{location.address}</p>
      )}
      {location.type === 'in_person' && maps !== null && (
        <a href={maps} target="_blank" rel="noreferrer" className="mt-0.5 inline-block text-xs underline" data-testid="booking-location-maps">
          {t('booking.public.openMaps')}
        </a>
      )}
      {location.notes && <p className="mt-1 whitespace-pre-line text-xs text-gray-500">{location.notes}</p>}
    </div>
  );
}
